import type {ReactElement} from 'react';
import type {Editor} from 'grapesjs';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import {EMAIL_MAKER_COMMANDS} from '@EmailMakerReact/email-maker-react.constants';
import {EmbeddedEditorElementsToHide} from '@Libraries/embedded-editor';
import {isEmbedElementHidden} from '@EmailMakerReact/lib/email-maker-embedded-editor-library';
import {PreviewButton} from '../preview-button/preview-button';
import {SaveStatus} from '../save-status/save-status';
import {UndoRedoButtons} from '../undo-redo-buttons/undo-redo-buttons';
import type {EmailMakerEmbeddedNavProps} from './email-maker-embedded-nav.types';
import styles from './email-maker-embedded-nav.module.scss';

function renderEmbedPreviewSlot(store: EmailMakerImmutableStore, editor: Editor | null): ReactElement | null {
  if (isEmbedElementHidden(store, EmbeddedEditorElementsToHide.PREVIEW)) {
    return null;
  }
  return <PreviewButton editorViewport={EditorViewport.DESKTOP} editor={editor} />;
}

export function EmailMakerEmbeddedNav({editor, actions, canUndo, canRedo, store}: EmailMakerEmbeddedNavProps): ReactElement {
  function handleBackClick(): void {
    if (editor) {
      editor.Commands.run(EMAIL_MAKER_COMMANDS.CLOSE_EMBEDDED_EDITOR, {isSilentClose: false});
    }
  }

  function handleSaveClick(): void {
    void actions.save();
  }

  function handleDoneClick(): void {
    if (editor) {
      editor.Commands.run(EMAIL_MAKER_COMMANDS.SAVE_AND_CLOSE_EMBEDDED_EDITOR);
    }
  }

  return (
    <header className={styles.navWrapper}>
      <nav className={styles.navbar} aria-label={window.i18next.t('pmwjs_email_maker_nav_aria')}>
        <div className={styles.left}>
          <Icon
            ariaLabel={window.i18next.t('pmwjs_back')}
            className={styles.backButton}
            icon="icon-arrow-left"
            isUsedAsButton
            onClick={handleBackClick}
            shape={IconShape.SQUARE}
            size={IconSize.SIZE_ICON_20}
            type={IconType.DARK_GHOST}
          />
          <Text val={window.i18next.t('pmwjs_edit_email')} size={TextSize.MD} bold className={styles.title} />
          <div className={styles.undoRedoSlot}>
            <UndoRedoButtons actions={actions} canUndo={canUndo} canRedo={canRedo} />
          </div>
        </div>

        <div className={styles.right}>
          <SaveStatus onErrorIndicatorClick={handleSaveClick} />
          {renderEmbedPreviewSlot(store, editor)}
          <Button
            icon="icon-arrow-right"
            iconPlacement={IconPlacement.RIGHT}
            onClick={handleDoneClick}
            size={ButtonSize.MEDIUM}
            text={store.nextBtnCaption}
            type={ButtonType.SECONDARY}
          />
        </div>
      </nav>
    </header>
  );
}
